import { useCallback, useMemo } from "react";

import { useLocalization } from "@fluent/react";
import { Count, Tabs } from "@narmi/design_system";
import { Navigate, useSearchParams } from "react-router-dom";

import { useUserFeatures } from "../../../components/contexts/UserFeaturesContext";
import PageLayout from "../../components/PageLayout";
import {
  ReceiptBucket,
  parseReceiptSort,
  useReceiptsList,
} from "../receipts/hooks/useReceiptsList";

import styles from "./TransactionsPage.module.scss";
import LedgerTab from "./components/LedgerTab";
import ReceiptInboxTab from "./components/ReceiptInboxTab";
import {
  LIVE_LEDGER_LEDGER_TAB_SLUG,
  LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY,
  LIVE_LEDGER_RECEIPTS_TAB_SLUG,
  LIVE_LEDGER_TAB_IDS,
  LIVE_LEDGER_TAB_QUERY_KEY,
} from "./constants";

const parseTabIndex = (raw: string | null, visibleTabSlugs: string[]) => {
  const index = visibleTabSlugs.indexOf(raw ?? "");
  return index === -1 ? 0 : index;
};

type TransactionsPageProps = {
  /** Institution's configured support reply time, shown in the report-an-issue dialog. */
  replyTime: string;
};

const TransactionsPage = ({ replyTime }: TransactionsPageProps) => {
  const { l10n } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    live_ledger: liveLedgerEnabled = false,
    receipts_enabled: receiptsEnabled = false,
  } = useUserFeatures();
  const showLedgerTab = liveLedgerEnabled;
  const showReceiptsTab = receiptsEnabled;
  const visibleTabSlugs = useMemo(() => {
    const slugs: string[] = [];
    if (showLedgerTab) slugs.push(LIVE_LEDGER_LEDGER_TAB_SLUG);
    if (showReceiptsTab) slugs.push(LIVE_LEDGER_RECEIPTS_TAB_SLUG);
    return slugs;
  }, [showLedgerTab, showReceiptsTab]);

  const activeTabIndex = parseTabIndex(
    searchParams.get(LIVE_LEDGER_TAB_QUERY_KEY),
    visibleTabSlugs
  );
  const activeTabSlug = visibleTabSlugs[activeTabIndex];

  // Same query key ReceiptInboxTab's own pending-list subscription uses, so
  // React Query serves both from one fetch.
  const sort = parseReceiptSort(
    searchParams.get(LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY)
  );
  const { counts } = useReceiptsList({
    bucket: ReceiptBucket.PENDING_REVIEW,
    sort,
    enabled: showReceiptsTab,
  });
  const pendingCount = counts?.pending ?? 0;

  const handleTabChange = useCallback(
    (index: number) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          const selectedSlug = visibleTabSlugs[index];
          if (selectedSlug === LIVE_LEDGER_LEDGER_TAB_SLUG) {
            updated.delete(LIVE_LEDGER_TAB_QUERY_KEY);
          } else if (selectedSlug === LIVE_LEDGER_RECEIPTS_TAB_SLUG) {
            updated.set(LIVE_LEDGER_TAB_QUERY_KEY, selectedSlug);
          }
          return updated;
        },
        // Replace so tab switches don't stack history entries.
        { replace: true }
      );
    },
    [setSearchParams, visibleTabSlugs]
  );

  if (visibleTabSlugs.length === 0) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="nds-typography" data-testid="transactions-page">
      <PageLayout>
        <div>
          <h1>{l10n.getString("live-ledger-page-title")}</h1>
          <div className={styles.tabs}>
            <Tabs
              selectedIndex={activeTabIndex}
              onTabChange={handleTabChange}
              testId="live-ledger-tabs"
            >
              <Tabs.List>
                {showLedgerTab && (
                  <Tabs.Tab
                    label={l10n.getString("live-ledger-tab-ledger")}
                    tabId={LIVE_LEDGER_TAB_IDS.ledger}
                    testId={LIVE_LEDGER_TAB_IDS.ledger}
                  />
                )}
                {showReceiptsTab && (
                  <Tabs.Tab
                    label={l10n.getString("live-ledger-tab-receipt-inbox")}
                    tabId={LIVE_LEDGER_TAB_IDS.receiptInbox}
                    testId={LIVE_LEDGER_TAB_IDS.receiptInbox}
                    renderEndContent={
                      pendingCount > 0
                        ? (isSelected) => (
                            <Count
                              value={pendingCount}
                              kind={isSelected ? "theme" : "neutral"}
                            />
                          )
                        : undefined
                    }
                  />
                )}
              </Tabs.List>
              {showLedgerTab && (
                <Tabs.Panel
                  tabId={LIVE_LEDGER_TAB_IDS.ledger}
                  testId="live-ledger-panel-ledger"
                >
                  <LedgerTab replyTime={replyTime} />
                </Tabs.Panel>
              )}
              {showReceiptsTab && (
                <Tabs.Panel
                  tabId={LIVE_LEDGER_TAB_IDS.receiptInbox}
                  testId="live-ledger-panel-receipt-inbox"
                >
                  <ReceiptInboxTab
                    isActive={activeTabSlug === LIVE_LEDGER_RECEIPTS_TAB_SLUG}
                  />
                </Tabs.Panel>
              )}
            </Tabs>
          </div>
        </div>
      </PageLayout>
    </div>
  );
};

export default TransactionsPage;
